/**
 * @file        src/pages/hotel/CancelBookingModal.jsx
 * @description Admin-initiated hotel cancellation. Previews the refund from the
 *              stored/live hotel policy (via the customer booking backend /
 *              TripJack), then commits. Two-step: preview → confirm, so the admin
 *              sees the refund before anything irreversible happens.
 *
 *              Hotel cancellation is ASYNCHRONOUS — the booking backend returns
 *              "Cancellation Requested" when TripJack accepts the request (an agent
 *              completes it later) and only "Cancelled" on a terminal status. We
 *              show exactly what it reports; never fake a completed cancellation.
 * @author      Kinetika Labs
 * @created     2026-07-16
 * @changelog
 *   - 2026-08-04 Claude — CR E2: the cancellation reason is now REQUIRED (min 3
 *                          trimmed characters) — the input is marked required,
 *                          Confirm is disabled until it's valid, and an inline
 *                          error explains why. The backend enforces this too
 *                          (defence-in-depth, not the only guard).
 *   - 2026-08-04 Claude — Cancel now also invalidates the Dashboard's Recent
 *                          Transactions/summary cache, not just this page's own
 *                          lists — a cancellation used to leave the dashboard
 *                          showing the pre-cancel ("Success") status until its
 *                          30s staleTime happened to expire.
 *   - 2026-07-16 Claude — Initial (Hotel Booking List Cancel action).
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 *   - 2026-09-15 Claude — Reason input capped at 255 chars — the cancel route rejects longer (a 400 the admin could not see coming).
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import { cancelPreview, cancelSubmit } from '../../api/hotelBookings';
import { qk } from '../../api/queryKeys';
import { apiErrorMessage } from '../../api/client';

const inr2 = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CancelBookingModal({ booking, onClose }) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState('loading'); // loading | preview | blocked | submitting | done | error
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  // CR E2: a cancellation reason is mandatory — trimmed, minimum 3 characters, so
  // whitespace alone can't pass as a reason.
  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= 3;
  const reasonTouched = reason.length > 0;

  useEffect(() => {
    let alive = true;
    cancelPreview(booking.id)
      .then((res) => {
        if (!alive) return;
        // The booking backend returns { cancellable:false, message } when the stay
        // is already cancelled / not confirmed / past — surface that, don't offer a
        // confirm button that would only 409.
        if (res && res.cancellable === false) { setInfo(res); setPhase('blocked'); }
        else { setInfo(res); setPhase('preview'); }
      })
      .catch((e) => {
        if (alive) { setErr(apiErrorMessage(e, 'Could not load cancellation.')); setPhase('error'); }
      });
    return () => { alive = false; };
  }, [booking.id]);

  const submit = () => {
    if (!reasonValid) { setErr('Please provide a cancellation reason (at least 3 characters).'); return; }
    setPhase('submitting'); setErr('');
    cancelSubmit(booking.id, trimmedReason)
      .then((res) => {
        setInfo(res); setPhase('done');
        // Refresh every hotel list/summary the status change touches.
        qc.invalidateQueries({ queryKey: ['hotel-bookings'] });
        qc.invalidateQueries({ queryKey: ['hotel-cancellations'] });
        // Timeline card: the cancellation and its refund event are new history.
        qc.invalidateQueries({ queryKey: ['booking-logs'] });
        qc.invalidateQueries({ queryKey: qk.dashboard.recent });
        qc.invalidateQueries({ queryKey: qk.dashboard.summary });
      })
      .catch((e) => { setErr(apiErrorMessage(e, 'Cancellation failed.')); setPhase('error'); });
  };

  // Preview may or may not carry a quotable refund: refundKnown=false means the
  // policy couldn't be priced and our team confirms the refund per the hotel's terms.
  const refundKnown = info?.refundKnown !== false;

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 480 }}>
        <div className="lk-modal-head">
          <h3>Cancel Hotel Booking · #{booking.id}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          {phase === 'loading' && <div className="empty">Fetching the hotel's cancellation policy…</div>}

          {phase === 'blocked' && (
            <>
              <p>{info?.message || 'This booking cannot be cancelled online.'}</p>
              <div className="form-actions"><button className="btn ghost" onClick={onClose}>Close</button></div>
            </>
          )}

          {phase === 'preview' && (
            <>
              <p className="muted">
                {booking.booking_reference}
                {booking.hotel_name ? ` · ${booking.hotel_name}` : ''} · confirm before cancelling.
              </p>
              <div className="det-fare">
                {refundKnown ? (
                  <>
                    <div className="det-fare-row"><span>Amount paid</span><span>{inr2(info?.totalPaid)}</span></div>
                    {info?.penalty != null ? <div className="det-fare-row"><span>Hotel penalty</span><span>{inr2(info.penalty)}</span></div> : null}
                    <div className="det-fare-row"><span>Refund amount</span><span><b>{inr2(info?.refundAmount)}</b></span></div>
                    {info?.refundable === false ? <div className="field-error">Non-refundable rate — no refund will be issued.</div> : null}
                  </>
                ) : (
                  <div className="det-fare-row">
                    <span>Refund</span>
                    <span>{info?.message || 'Confirmed by our team as per the hotel policy.'}</span>
                  </div>
                )}
              </div>
              <div className="field full">
                <label>Reason <span style={{ color: 'var(--danger, #e5484d)' }}>*</span></label>
                <input
                  required
                  maxLength={255}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this booking being cancelled?"
                />
                {reasonTouched && !reasonValid ? (
                  <div className="field-error">Please provide a reason (at least 3 characters).</div>
                ) : null}
              </div>
              <p className="muted" style={{ fontSize: '.85em' }}>
                Cancellation is sent to the hotel and may sit as <b>Cancellation Requested</b> until an
                agent completes it. Refund is processed by our team within 5–7 business days.
              </p>
              {err ? <div className="field-error">{err}</div> : null}
              <div className="form-actions">
                <button className="btn ghost" onClick={onClose}>Keep booking</button>
                <button className="btn danger" onClick={submit} disabled={!reasonValid}>Confirm cancel</button>
              </div>
            </>
          )}

          {phase === 'submitting' && <div className="empty">Submitting cancellation…</div>}

          {phase === 'done' && (
            <>
              <p><b>Cancellation submitted.</b></p>
              <div className="det-fare">
                <div className="det-fare-row"><span>Status</span><span>{info?.status || 'Cancellation Requested'}</span></div>
                {info?.refundAmount != null ? <div className="det-fare-row"><span>Refund</span><span>{inr2(info.refundAmount)}</span></div> : null}
              </div>
              {info?.message ? <p className="muted" style={{ fontSize: '.85em' }}>{info.message}</p> : null}
              <div className="form-actions"><button className="btn primary" onClick={onClose}>Done</button></div>
            </>
          )}

          {phase === 'error' && (
            <>
              <div className="field-error">{err}</div>
              <div className="form-actions"><button className="btn ghost" onClick={onClose}>Close</button></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
